import { isNil } from 'es-toolkit';
import { useActiveHouseholdId } from 'src/hooks/use-active-household-id';
import { isAdvisor } from 'src/utils/account';

import { useAuth } from '@savvy/deprecated-shared/hooks/use-auth';
import { useQueryWithErrorLogging } from '@savvy/graphql-hooks/useQueryWithErrorLogging';

import type { ClientPortalFundingPhaseState } from '../utils/funding-phase';
import { ClientPortalOnboardingActionsQuery } from '../api/client-onboarding-actions-query';
import { ClientPortalFundingStateQuery } from '../api/funding-state-query';
import { ClientPortalTransferDestinationQuery } from '../api/transfer-destination-query';
import { clientOnboardingActionsComplete } from '../utils/client-onboarding-actions';
import { fundingPhaseState, isTransferringPhase, transferDestinationCustodian } from '../utils/funding-phase';

export type { ClientPortalFundingPhaseState };

export function useClientPortalFundingPhase(): ClientPortalFundingPhaseState {
  const auth = useAuth();
  const { activeHouseholdId, canQuery } = useActiveHouseholdId();
  const isClient = Boolean(auth.user) && !isAdvisor(auth);

  const { data, loading } = useQueryWithErrorLogging(ClientPortalFundingStateQuery, {
    variables: { householdId: activeHouseholdId },
    skip: !canQuery || !isClient,
  });

  const phase = data?.householdFundingState.phase ?? null;
  const awaitsAccountOpening = phase === 'AWAITING_ACCOUNT_OPENING';

  const { data: onboardingActionsData, loading: onboardingActionsLoading } = useQueryWithErrorLogging(
    ClientPortalOnboardingActionsQuery,
    {
      variables: { householdId: activeHouseholdId },
      skip: !canQuery || !isClient || !awaitsAccountOpening,
    },
  );

  const { data: destinationData } = useQueryWithErrorLogging(ClientPortalTransferDestinationQuery, {
    variables: { id: auth.user?.id ?? '', householdId: activeHouseholdId },
    skip: !canQuery || !isClient || isNil(auth.user?.id) || !isTransferringPhase(phase),
  });

  return fundingPhaseState({
    isClient,
    canQuery,
    queryLoading: loading,
    phase,
    clientActionsComplete: clientOnboardingActionsComplete(onboardingActionsData?.tasks),
    clientActionsLoading: awaitsAccountOpening && onboardingActionsLoading,
    destinationCustodian: transferDestinationCustodian(destinationData?.portfolio.accounts ?? []),
    accounts: data?.householdFundingState.accounts,
  });
}
